import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import { Elements } from '@stripe/react-stripe-js';
import Button from '@ui/Button';
import { getNumber } from '@util/createListingHelpers';
import useRealTimeBids from '@util/hooks/auctions/useRealtimeBids';
import { formatCurrency, isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useConfirmationModal } from 'context/ConfirmationModalContext';
import { useStripeContext } from 'context/StripeContext';
import { useToastContext } from 'context/ToastContext';
import { ProductDocument } from 'models/product';
import { usePathname } from 'next/navigation';
import { useRouter } from '@bprogress/next';
import { useState } from 'react';
import PlaceABidModal from './PlaceABidModal';
import UpdateBidModal from './UpdateBidModal';
import MobilePlaceABidModal from './MobilePlaceABidModal';

export const PlaceABidButton = ({ product }: { product: ProductDocument }) => {
  const { user, userDoc } = useAuth();
  const { stripePromise } = useStripeContext();
  const router = useRouter();
  const pathname = usePathname();
  const [openModal, setOpenModal] = useState(false);
  const { highestBid, usersBids } = useRealTimeBids(product.id, userDoc?.uid);
  const [updateBidPrice, setUpdateBidPrice] = useState('');
  const { showErrorToast } = useToastContext();

  const hasExistingBid = usersBids.length > 0;
  return (
    <>
      {hasExistingBid ? (
        <div className="flex w-full gap-4">
          <FormCurrencyInputFancy
            value={updateBidPrice}
            className="w-full !max-w-none"
            onChange={(e) => setUpdateBidPrice(e.target.value)}
          />
          <Button
            type="secondary"
            text="Update bid"
            onClick={() => {
              if (updateBidPrice) {
                if (getNumber(updateBidPrice) < highestBid.bid_price + 1) {
                  showErrorToast(
                    `Your bid must be at least ${formatCurrency(
                      1
                    )} higher than the current bid!`
                  );
                  return;
                }
                setOpenModal(true);
              } else {
                showErrorToast('Please enter a valid bid price');
              }
            }}
          />
        </div>
      ) : (
        <Button
          type="secondary"
          text={user?.isAnonymous || !user ? 'Log in to bid' : 'Place a bid'}
          className="!h-[6rem] !rounded-full !py-6 !text-[1.8rem] !font-semibold"
          width="fluid"
          onClick={() => {
            if (!user || user.isAnonymous) {
              // waiting for userDoc
              router.push(`/fetch-user?redirect=${pathname}`);
            } else {
              setOpenModal(true);
            }
          }}
        />
      )}

      <Elements stripe={stripePromise}>
        {isMobile() && !hasExistingBid ? (
          <MobilePlaceABidModal
            show={openModal}
            dismiss={() => {
              setOpenModal(false);
            }}
            product={product}
          />
        ) : !hasExistingBid ? (
          <PlaceABidModal
            isOpen={openModal}
            dismiss={() => {
              setOpenModal(false);
            }}
            product={product}
          />
        ) : (
          <UpdateBidModal
            product={product}
            isOpen={openModal}
            dismiss={() => {
              setOpenModal(false);
              setUpdateBidPrice('');
            }}
            updateBidPrice={getNumber(updateBidPrice)}
          />
        )}
      </Elements>
    </>
  );
};
